// The facilitator's single entry point for reaching their forum, opened by
// the "Message Forum" button on the My Forum page. Two paths:
//   - Group chat: jump into the forum's default thread in Messages.
//   - Announcement: compose here; it posts to that same thread (in-app +
//     push via the messaging signal), replacing the old composer card.
// Admin org-wide pushes stay on the separate Broadcast page.

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageSquare, Megaphone, Send, X, ChevronLeft } from 'lucide-react';
import toast from 'react-hot-toast';
import axios from 'axios';
import { messagingAPI } from '../api/client';
import MicButton from './MicButton';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  forumName: string;
  // The forum's default-chat thread id (from the roster payload). When null
  // the forum has no chat yet and both paths are disabled.
  threadId: number | null;
  // Announcements are facilitator/admin-only; members only get the chat path.
  canAnnounce: boolean;
}

function extractApiError(error: unknown, fallback: string): string {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data;
    if (typeof data === 'string') return data;
    if (data && typeof data === 'object') {
      const record = data as Record<string, unknown>;
      const candidate =
        record.non_field_errors ?? record.error ?? record.detail ?? Object.values(record)[0];
      if (Array.isArray(candidate)) return String(candidate[0]);
      if (candidate) return String(candidate);
    }
  }
  return fallback;
}

export default function MessageForumModal({
  isOpen,
  onClose,
  forumName,
  threadId,
  canAnnounce,
}: Props) {
  const navigate = useNavigate();
  const [mode, setMode] = useState<'choose' | 'announce'>('choose');
  const [content, setContent] = useState('');
  const [sending, setSending] = useState(false);

  if (!isOpen) return null;

  const close = () => {
    setMode('choose');
    setContent('');
    onClose();
  };

  const openChat = () => {
    if (threadId === null) {
      toast.error('This Forum has no group chat yet.');
      return;
    }
    close();
    navigate(`/messages?thread=${threadId}`);
  };

  const handleSendAnnouncement = async () => {
    const body = content.trim();
    if (!body || sending || threadId === null) return;
    setSending(true);
    try {
      await messagingAPI.sendToThread(threadId, body);
      toast.success('Announcement posted to the Forum chat (in-app + push).');
      close();
    } catch (error) {
      toast.error(extractApiError(error, 'Could not post the announcement.'));
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="message-forum-title"
      onClick={close}
    >
      <div
        className="bg-white rounded-2xl shadow-xl w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 p-6 pb-0">
          <h2 id="message-forum-title" className="text-lg font-semibold text-navy">
            {mode === 'announce' ? `Announce to ${forumName}` : `Message ${forumName}`}
          </h2>
          <button
            onClick={close}
            aria-label="Close"
            className="p-1.5 text-gray-400 hover:text-navy rounded-lg transition"
          >
            <X size={18} />
          </button>
        </div>

        {mode === 'choose' ? (
          <div className="p-6 space-y-3">
            <button
              onClick={openChat}
              className="w-full text-left border border-gray-200 hover:border-gold rounded-xl p-4 transition flex items-start gap-3"
            >
              <MessageSquare size={20} className="text-navy mt-0.5 flex-shrink-0" />
              <span>
                <span className="block font-semibold text-navy">Group chat</span>
                <span className="block text-sm text-gray-500 mt-0.5">
                  Open the Forum's conversation in Messages.
                </span>
              </span>
            </button>
            {canAnnounce && (
              <button
                onClick={() => setMode('announce')}
                className="w-full text-left border border-gray-200 hover:border-gold rounded-xl p-4 transition flex items-start gap-3"
              >
                <Megaphone size={20} className="text-gold mt-0.5 flex-shrink-0" />
                <span>
                  <span className="block font-semibold text-navy">Announcement</span>
                  <span className="block text-sm text-gray-500 mt-0.5">
                    Post an update to everyone — lands in the Forum chat and
                    pushes to members' devices.
                  </span>
                </span>
              </button>
            )}
          </div>
        ) : (
          <div className="p-6 space-y-3">
            <button
              onClick={() => setMode('choose')}
              className="inline-flex items-center gap-1 text-sm text-navy font-medium"
            >
              <ChevronLeft size={15} />
              Back
            </button>
            <div className="relative">
              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                rows={4}
                autoFocus
                placeholder={`Share an update with everyone in ${forumName}…`}
                className="w-full px-3 py-2 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-navy focus:border-transparent outline-none text-sm"
              />
              <MicButton
                value={content}
                onChange={setContent}
                className="absolute bottom-2 right-2"
              />
            </div>
            <p className="text-xs text-gray-400">
              Delivered in-app and by push notification. SMS and email are
              coming soon.
            </p>
            <div className="flex justify-end">
              <button
                onClick={handleSendAnnouncement}
                disabled={!content.trim() || sending || threadId === null}
                className="inline-flex items-center gap-2 px-4 py-2 bg-navy text-white rounded-lg hover:bg-navy-dark transition disabled:opacity-50"
              >
                <Send size={16} />
                {sending ? 'Posting…' : 'Post announcement'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
